import { Link } from 'react-router-dom'
import {
  ArrowRight,
  Building2,
  Church,
  Globe2,
  HeartHandshake,
  LayoutTemplate,
  School,
  ShieldCheck,
  Sparkles,
} from 'lucide-react'

import { brandifyNode } from '@/components/brand/kindlumen'
import { SeoHead } from '@/components/seo/seo-head'
import { Button } from '@/components/ui/button'
import { Card, CardDescription, CardTitle } from '@/components/ui/card'
import { SectionHeading } from '@/components/ui/section-heading'
import { ZERO_FEE_BADGE } from '@/data/pricing-content'
import { createMetadata, marketingSeoPages } from '@/lib/seo'

const audienceCards = [
  {
    icon: Church,
    title: 'Faith-based organizations',
    description: 'Recurring giving for mosques, churches, and community orgs.',
  },
  {
    icon: Building2,
    title: 'Community nonprofits',
    description: 'Add a donation form to an existing website and Stripe account.',
  },
  {
    icon: School,
    title: 'Schools and education',
    description: 'Annual giving and campaigns with simple receipts.',
  },
  {
    icon: Globe2,
    title: 'Relief and humanitarian',
    description: 'Urgent appeals with one-time and recurring gifts.',
  },
] as const

const solutionPrinciples = [
  {
    icon: Sparkles,
    title: 'A clear setup path',
    description: 'Connect Stripe, build your form, and add the generated two-line embed snippet.',
  },
  {
    icon: LayoutTemplate,
    title: 'A focused donor experience',
    description: 'A mobile-ready flow for the gift amount, donor details, and Stripe payment.',
  },
  {
    icon: ShieldCheck,
    title: 'Branded to your org',
    description: 'Your logo and colors — and donations paid straight into your own Stripe account, never held by KindLumen.',
  },
] as const

const workflowCards = [
  {
    title: 'Works with your existing site',
    description: 'Add KindLumen to a compatible page without rebuilding the rest of your website.',
    points: ['WordPress plugin or embed', 'Site-builder plans that permit custom scripts'],
  },
  {
    title: 'You build the site, Kind Lumen handles donations.',
    description: 'Build donation forms for your nonprofit clients. Invite them to connect Stripe, then finish the launch.',
    points: ['No shared Stripe credentials', 'Get notified when your client is ready'],
  },
  {
    title: 'Grows with you',
    description: 'Start with one form. Expand to recurring programs and multiple campaigns.',
    points: [ZERO_FEE_BADGE, 'No volume thresholds or tiers'],
  },
] as const

const outcomeCards = [
  'Form, checkout, receipts, and records in one flow',
  'A generated two-line embed and a hosted donation link',
  'Optional monthly giving through Stripe subscriptions',
  'Donation and donor records in the dashboard',
] as const

const metadata = createMetadata(marketingSeoPages.solutions)

export default function SolutionsPage() {
  return (
    <div className="space-y-20 pb-20 pt-10 sm:space-y-24">
      <SeoHead metadata={metadata} />
      <section className="marketing-shell">
        <div className="grid gap-8 xl:grid-cols-[1.02fr_0.98fr] xl:items-start">
          <div className="space-y-8 py-4 lg:pr-8">
            <div className="space-y-5">
              <div className="eyebrow">Solutions</div>
              <div className="space-y-4">
                <h1 className="max-w-3xl text-balance text-[2.45rem] font-semibold leading-[1.1] sm:text-[3.35rem] sm:leading-[1.06]">
                  A free donation form for the website you already have.
                </h1>
                <p className="max-w-2xl text-base leading-7 text-ink-soft sm:text-lg sm:leading-8">
                  Add a free Stripe donation form with one-time and recurring giving without replacing your website. Existing donor and subscription data are not migrated automatically.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button asChild size="lg">
                <Link to="/signup">
                  Start fundraising
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="secondary">
                <Link to="/features">View features</Link>
              </Button>
            </div>
          </div>

          <div className="panel-dark overflow-hidden p-8 lg:p-10">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-white/80">
                Why this matters
              </div>
              <h2 className="max-w-lg text-3xl font-semibold leading-[1.18] text-white sm:text-[2.25rem]">
                Keep the path to giving clear.
              </h2>
            </div>

            <div className="mt-8 grid gap-4">
              {outcomeCards.map((item) => (
                <div key={item} className="rounded-[24px] border border-white/10 bg-white/10 px-5 py-4">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-[16px] bg-white/10">
                      <HeartHandshake className="h-4 w-4 text-sky" />
                    </div>
                    <p className="text-sm leading-6 text-white/75">{brandifyNode(item, 'inverse')}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="marketing-shell">
        <SectionHeading eyebrow="Who it is for" title="Flexible across nonprofit models." />

        <div className="mt-8 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {audienceCards.map((item) => (
            <Card key={item.title} className="h-full">
              <div className="flex h-11 w-11 items-center justify-center rounded-[18px] bg-brand/10 text-brand">
                <item.icon className="h-5 w-5" />
              </div>
              <CardTitle className="mt-5 text-xl">{item.title}</CardTitle>
              <CardDescription className="mt-4">{item.description}</CardDescription>
            </Card>
          ))}
        </div>
      </section>

      <section className="marketing-shell">
        <div className="grid gap-6 xl:grid-cols-[1.02fr_0.98fr]">
          <div className="panel p-8 lg:p-10">
            <SectionHeading
              eyebrow="What teams need most"
              title="Start with a simpler donor experience."
            />

            <div className="mt-8 grid gap-4">
              {solutionPrinciples.map((item) => (
                <div key={item.title} className="rounded-[24px] border border-line bg-surface-strong px-5 py-5">
                  <div className="flex items-start gap-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[18px] bg-brand/10 text-brand">
                      <item.icon className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-lg font-semibold leading-[1.24] text-ink">{item.title}</p>
                      <p className="mt-3 text-sm leading-6 text-ink-soft">{item.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="panel-dark overflow-hidden p-8 lg:p-10">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-white/80">
                Operating model
              </div>
              <h2 className="max-w-lg text-3xl font-semibold leading-[1.18] text-white sm:text-[2.25rem]">
                One system as campaigns grow.
              </h2>
            </div>

            <div className="mt-8 grid gap-4">
              {workflowCards.map((item) => (
                <div key={item.title} className="rounded-[24px] border border-white/10 bg-white/10 px-5 py-5">
                  <p className="text-lg font-semibold leading-[1.24] text-white">{item.title}</p>
                  <p className="mt-3 text-sm leading-6 text-white/70">{item.description}</p>
                  <div className="mt-4 grid gap-2">
                    {item.points.map((point) => (
                      <div key={point} className="rounded-[18px] border border-white/10 bg-white/5 px-4 py-3 text-sm leading-6 text-white/70">
                        {brandifyNode(point, 'inverse')}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="marketing-shell">
        <div className="panel-dark overflow-hidden p-8 lg:p-10">
          <div className="grid gap-8 lg:grid-cols-[1fr_0.8fr] lg:items-end">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-white/80">
                Start today
              </div>
              <h2 className="max-w-2xl text-4xl font-semibold leading-[1.18] text-white sm:text-[2.8rem]">
                Start with a cleaner giving experience.
              </h2>
            </div>

            <div className="flex flex-wrap gap-3 lg:justify-end">
              <Button asChild size="lg">
                <Link to="/signup">Start free</Link>
              </Button>
              <Button asChild size="lg" variant="dark">
                <Link to="/pricing">View pricing</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export const Component = SolutionsPage
